import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? articles[slug] : undefined;

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Articolo non trovato</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">Torna alla homepage</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-sm border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="text-xl md:text-2xl font-heading font-bold text-foreground-950 whitespace-nowrap">
            Itaobuy <span className="text-primary-500">Spreadsheet</span>
          </Link>
          <div className="flex items-center gap-3 md:gap-4">
            <Link to="/" className="hidden md:inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-4-line text-sm"></i>
              </span>
              Home
            </Link>
            <Link to="/articolo/guida-itaobuy-spreadsheet" className="hidden md:inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line text-sm"></i>
              </span>
              Guida Shopping
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              itaobuy sheet
            </a>
          </div>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.description,
          "author": { "@type": "Organization", "name": "Itaobuy Spreadsheet Italia" },
          "datePublished": article.datePublished,
          "dateModified": article.datePublished,
          "inLanguage": "it",
          "publisher": { "@type": "Organization", "name": "Itaobuy Spreadsheet Italia", "url": "https://itaobuyspread.asia" }
        }) }} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://itaobuyspread.asia" },
            { "@type": "ListItem", "position": 2, "name": article.title, "item": `https://itaobuyspread.asia/articolo/${article.slug}` }
          ]
        }) }} />

        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium mb-8 whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line text-sm"></i>
          </span>
          Torna alla homepage
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">{article.tag}</span>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
            {article.subtitle}
          </p>
        </header>

        <div className="mb-10 rounded-xl overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.imageAlt}
            title={article.title}
            className="w-full h-64 md:h-80 object-cover"
          />
        </div>

        <div className="space-y-10">
          {article.sections.map((section, idx) => (
            <section key={idx}>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                {section.heading}
              </h2>
              {section.content.map((para, pIdx) => (
                <p key={pIdx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {para}
                </p>
              ))}
            </section>
          ))}
        </div>

        <div className="mt-12 p-6 md:p-8 rounded-xl bg-accent-50 border border-accent-200">
          <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">Conclusione</h3>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
            {article.conclusion}
          </p>
          <a
            href={article.conclusionCtaUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-block px-6 py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {article.conclusionCta}
          </a>
        </div>

        <div className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200/70">
          <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">Link utili</h3>
          <ul className="space-y-2">
            {article.relatedLinks.map((link, idx) => (
              <li key={idx}>
                {link.url.startsWith('http') ? (
                  <a href={link.url} target="_blank" rel="noopener nofollow" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
                    {link.label}
                  </a>
                ) : (
                  <Link to={link.url} className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 pt-8 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap">
            ← Torna alla homepage
          </Link>
          <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap">
            itaobuy sheet
          </a>
        </div>
      </article>

      <footer className="bg-background-200/70 border-t border-background-300/60 py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <p className="text-xs text-foreground-500">
            © 2026 Itaobuy Spreadsheet Italia. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </main>
  );
}